import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { usePartialStateQueuedSaver } from "@/Common/CustomHooks/usePartialStateQueuedSaver";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { RadioButtonv2 } from "../Inputs/RadioButtonv2";
import { CallCenterOptionsState } from "./CallCenterSettings.types";

import styles from "./CallCenterSettings.module.scss";

interface CallCenterOptionsProps {
    providerType: string,
    options: CallCenterOptionsState
    saveOptions: (savingOptions: CallCenterOptionsState) => Promise<void>
}

enum LoginOption {
    None = "none",
    Auto = "auto",
    Prompt = "prompt"
}

function LoginOptionFromSettings(settings: CallCenterOptionsState): LoginOption {
    if (!settings) {
        return LoginOption.Auto; // default to auto
    }
    if (settings.Active && settings.AskForAccountAtLogin) {
        return LoginOption.Prompt;
    }
    if (settings.Active && !settings.AskForAccountAtLogin) {
        return LoginOption.Auto;
    }
    return LoginOption.None;
}

function getPartialAutoOpenState(value: boolean = null): Partial<CallCenterOptionsState> {
    return ({ AutoOpenPatientOnSingleMatch: value });
}

function getPartialLoginState(loginOption: LoginOption = null): Partial<CallCenterOptionsState> {
    loginOption = loginOption ?? LoginOption.Auto;

    switch (loginOption) {
        case LoginOption.None:
            return ({ Active: false, AskForAccountAtLogin: false });
        case LoginOption.Prompt:
            return ({ Active: true, AskForAccountAtLogin: true });
        case LoginOption.Auto:
        default:
            return ({ Active: true, AskForAccountAtLogin: false });
    }
}

function CallCenterOptions(props: Readonly<CallCenterOptionsProps>): JSX.Element {
    const [options, savePartialOptions, isPartialOptionsSaving] = usePartialStateQueuedSaver(props.options, props.saveOptions, null);
    const { getText } = useTranslation();

    const isSavingLogin = isPartialOptionsSaving(getPartialLoginState());
    const isSavingAutoOpen = isPartialOptionsSaving(getPartialAutoOpenState());

    const loginOption = LoginOptionFromSettings(options);

    async function onLoginOptionChanged(option: LoginOption): Promise<void> {
        savePartialOptions(getPartialLoginState(option));
    }

    async function onAutoOpenPatientChanged(isChecked: boolean): Promise<void> {
        savePartialOptions(getPartialAutoOpenState(isChecked));
    }

    const autoOpenLabel = getText("CallCenterAutoSelectPatientCheckmarkLabel");

    const radioLoginTitle = getText("CallCenter_LoginOptions_Header");
    const autoLabel = getText("CallCenter_LoginOptions_Option_Auto", { providerType: props.providerType });
    const promptLabel = getText("CallCenter_LoginOptions_Option_Prompt", { providerType: props.providerType });
    const noneLabel = getText("CallCenter_LoginOptions_Option_None", { providerType: props.providerType });

    return (
        <div className={styles.options}>
            <RadioButtonv2
                title={radioLoginTitle}
                htmlName="CallCenterLoginOptions"
                items={[
                    new KeyValuePair(LoginOption.Auto, autoLabel),
                    new KeyValuePair(LoginOption.Prompt, promptLabel),
                    new KeyValuePair(LoginOption.None, noneLabel)
                ].toImmutable()}
                selectedItem={loginOption}
                onChangeCallback={selectedItem => onLoginOptionChanged(selectedItem.Key)}
                disabled={isSavingLogin}
                dataCy="CallCenterLoginOptionsList"
                wrapperClassName={styles.loginOptions}
                styleForEachradio={{ marginLeft: "20px" }}
            />
            <div className={styles.checkbox}>
                <CheckboxControl
                    id="callCenterAutoOpenPatientChanged"
                    dataCy="callCenterAutoOpenPatientChanged"
                    onChange={(onAutoOpenPatientChanged)}
                    label={autoOpenLabel}
                    checked={options.AutoOpenPatientOnSingleMatch}
                    disabled={isSavingAutoOpen}
                />
            </div>
        </div>
    );
}

export { CallCenterOptions };
